﻿import React, { useState, useEffect } from 'react';
import { Asset } from '../../models/asset';
import { LogBookEntry } from '../../models/Common/logbook-entry';
import { CrudFunctions } from '../../utilities/crud-functions';
import { NavLink } from 'react-router-dom';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { ItemBasicInformation } from '../../models/item-basic-information';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';

import DataGrid, {
    Column,
    FilterRow,
    HeaderFilter,
    SearchPanel,
    Paging,
    Toolbar,
    Button as ColumnButton,
    Item
} from 'devextreme-react/data-grid';

import TextArea from 'devextreme-react/text-area';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    asset: Asset;
    benchSheetId?: string;
    readOnly?: boolean;
    onClose: () => void;
}

export default function LogbookEntryComponent(props: Props) {
    const [logbookEntry, setLogbookEntry] =
        useState<LogBookEntry>();

    const [logbookEntries, setLogbookEntries] =
        useState<LogBookEntry[]>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getLogbookEntries();
    }, []);

    return (
        <div>
            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Asset Name</div>
                    <div className="pw-readonly-label">{props.asset && props.asset.assetName}</div>
                </div>
            </div>

            {!props.readOnly &&
                <div className="pw-form-unit">
                    <div className="pw-form-unit-label">Logbook Entry</div>
                    <TextArea id="logbookEntryTextArea"
                        height="5rem"
                        onValueChanged={logbookEntryChanged}
                        value={logbookEntry && logbookEntry.notes ? logbookEntry.notes : undefined} />
                </div>
            }

            <div data-testid="logbookEntriesDataGrid" className="pw-margin-top-1">
                <DataGrid height="26rem"
                    dataSource={logbookEntries ? logbookEntries : []}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >

                    <SearchPanel visible={true}
                        placeholder="Search..." />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='5rem'>
                        <ColumnButton hint="information" icon="info"
                            onClick={onLogbookInformationButtonClicked} />
                        <ColumnButton hint="go to bench sheet" render={gotoBenchSheetCellRender} />
                    </Column>

                    <Column dataField='entryNumber' width="7rem" caption="Entry #" dataType="number" allowEditing={false} />
                    <Column dataField='userFullName' width="8rem" caption="User" dataType="string" allowEditing={false} />

                    <Column dataField='createTimestamp' width="10rem"
                        format="MM/dd/yyyy HH:mm:ss" allowEditing={false}
                        caption="Created" dataType="date" />

                    <Column dataField='catalogNumber' width="10rem" caption="Catalog#" dataType="string" />
                    <Column dataField='batchName' width="10rem" dataType="string" />
                    <Column dataField='notes' dataType="string" />

                    <Toolbar>
                        <Item location='before' template='batchesToolbarTemplate'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Logbook Entries</div>
                            </div>
                        </Item>

                        <Item name="searchPanel" />
                    </Toolbar>
                </DataGrid>
            </div>

            <div className="pw-comp-row">
                <div className="pw-centered-buttons-container">
                    {props.readOnly &&
                        <Button id="closeButton" className="pw-button"
                            text="Close" type="success" hint="close dialog"
                            height='2.1rem' onClick={onCloseClick} />
                    }

                    {!props.readOnly &&
                        <>
                            <Button id="applySelectionButton" className="pw-button"
                                text="Save" type="success" hint="save log entry"
                                height='2.1rem' onClick={onSaveClick} />

                            <Button id="closeButton" className="pw-button"
                                text="Close" type="normal" hint="close dialog"
                                height='2.1rem' onClick={onCloseClick} />
                        </>
                    }
                </div>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    async function onSaveClick() {
        if (props.asset && logbookEntry && logbookEntry.notes) {
            logbookEntry.assetId = props.asset.assetId;
            logbookEntry.benchSheetId = props.benchSheetId;

            await saveLogbookEntry(logbookEntry)
                .then(() => {
                    getLogbookEntries();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Saving Logbook Entry");
                });
        }
    }

    function onCloseClick() {
        props.onClose();
    }

    function logbookEntryChanged(e: any) {
        setLogbookEntry({ ...logbookEntry, notes: e.value });
    }

    async function getLogbookEntries() {
        if (props.asset) {
            await CrudFunctions.getData(`common/logbookentries/${props.asset.assetId}`)
                .then(data => {
                    setLogbookEntries(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Logbook Entries");
                });
        }
    }

    async function saveLogbookEntry(logbookEntry: LogBookEntry) {
        return await CrudFunctions.postData("common/logbookentry", logbookEntry);
    }

    function gotoBenchSheetCellRender(e: any) {
        console.log("gotoBenchSheetCellRender");
        console.log(e);

        if (e.data) {
            let logbookEntry = e.data as LogBookEntry;

            if (logbookEntry && logbookEntry.technologyId && logbookEntry.benchSheetId) {
                let path = `/benchsheet/${logbookEntry.technologyId}/${logbookEntry.benchSheetId}`;

                return (
                    <NavLink to={path} className="datagrid-icon-link" target="_blank" rel="noopener noreferrer">
                        <span className="dx-icon-textdocument datagrid-icon column-button-margin"></span>
                    </NavLink>
                );
            }
            else
                return <div></div>;
        }

        return <div></div>;
    }

    function onLogbookInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const entry = e.row.data as LogBookEntry;

            if (entry) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: entry.logbookEntryId,
                    createTimestamp: entry.createTimestamp,
                    lastModifiedTimestamp: entry.modifyTimestamp,
                    createUser: entry.createUser,
                    modifyUser: entry.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}

